import React, { useState } from "react";
import { Fragment } from "react";
import { Link } from "react-router-dom";
import MarketNavbar from "../Marketplace/MarketNav/MarketNavbar";
import MarketplacePage from "../Marketplace/components/MarketplacePage";
import useMarketplaceShippingFees, {
  mergeShippingSettings,
} from "../Marketplace/libs/useMarketplaceShippingFees";
import { FaBarcode, FaBoxOpen } from "react-icons/fa";
import { GiBrokenPottery, GiSuperMushroom, GiTreeFace, GiBookmarklet, GiUnderwearShorts } from "react-icons/gi";
import "./RetunPolicy.css";

const SUPPORT_EMAIL = "infosupport@smarttransexpress.com";

const TABS = [
  { id: "instruction", label: "Return instructions" },
  { id: "eligible", label: "Non-eligible items" },
  { id: "faq", label: "FAQs" },
  { id: "requirement", label: "Return requirements" },
];

const Returnpolicy = () => {
  const [activeTab, setActiveTab] = useState("instruction");
  const { data: shippingSettings } = useMarketplaceShippingFees();
  const shipping = mergeShippingSettings(shippingSettings);

  return (
    <Fragment>
      <MarketNavbar />
      <MarketplacePage className="return-policy-page">
        <nav className="return-policy-breadcrumb" aria-label="Breadcrumb">
          <Link to="/market">Home</Link>
          <span>/</span>
          <span>{shipping.returnPolicyTitle}</span>
        </nav>

        <div className="return-policy">
          <header className="return-policy__header">
            <h1>Return &amp; refunds policy</h1>
            <p>
              At Smart Trans Express (STEX), we want every purchase to feel safe and
              straightforward. This page explains how returns, refunds, and exchanges work
              on our marketplace.
            </p>
            {shipping.returnPolicyText && (
              <p className="return-policy__highlight">{shipping.returnPolicyText}</p>
            )}
          </header>

          <div className="return-mother">
            <div className="return-tabs" role="tablist">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  className={activeTab === tab.id ? "active" : ""}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="return-card">
              {activeTab === "instruction" && (
                <div className="return-info">
                  <div className="wrapper">
                    {[
                      ["STEP 1", "Initiate return request", "Log in to your STEX account, go to My Orders, select the item(s) you want to return, and choose Return or Request Return."],
                      ["STEP 2", "Provide reason for return", "Choose the reason for your return and add any helpful details so we can process your request quickly."],
                      ["STEP 3", "Choose return method", "Select self-return or courier pickup based on what is available in your location."],
                      ["STEP 4", "Prepare return package", "Pack the item securely with original packaging and accessories where possible."],
                      ["STEP 5", "Generate return label", "If STEX provides a return label, print it and attach it clearly to the package."],
                    ].map(([step, title, body]) => (
                      <span key={step}>
                        <h3>{step}</h3>
                        <h4>{title}</h4>
                        <p>{body}</p>
                      </span>
                    ))}
                  </div>
                  <div className="wrapper">
                    {[
                      ["STEP 6", "Schedule return pickup", "Drop off at a STEX pickup station or schedule a courier pickup at a convenient time."],
                      ["STEP 7", "Track return status", "Use the tracking information provided to follow your return after dispatch."],
                      ["STEP 8", "Receive refund or exchange", "After inspection, we process your refund or exchange according to policy."],
                      ["STEP 9", "Confirmation notification", "You will receive email or SMS confirmation when your return is completed."],
                      ["STEP 10", "Contact customer support", `Need help? Email ${SUPPORT_EMAIL} and our team will assist you.`],
                    ].map(([step, title, body]) => (
                      <span key={step}>
                        <h3>{step}</h3>
                        <h4>{title}</h4>
                        <p>{body}</p>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === "eligible" && (
                <div className="non-eleigible">
                  <h3>Non-eligible items for return</h3>
                  <div className="card">
                    <div className="wrapper">
                      <span><FaBoxOpen /><p>Products altered or opened by unauthorized personnel without permission.</p></span>
                      <span><GiSuperMushroom /><p>Perishable goods unless a valid issue is raised at delivery with dispatcher confirmation.</p></span>
                      <span><GiTreeFace /><p>Products in beauty, health, and personal care categories.</p></span>
                      <span><GiBookmarklet /><p>Books and CDs.</p></span>
                    </div>
                    <div className="wrapper">
                      <span><FaBarcode /><p>Products with tampered or missing serial / UPC numbers.</p></span>
                      <span><GiBrokenPottery /><p>Products damaged due to misuse.</p></span>
                      <span><GiUnderwearShorts /><p>Jewellery, innerwear, bed sheets, lingerie, and socks.</p></span>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "faq" && (
                <div className="return-faq">
                  <h3>Frequently asked questions</h3>
                  <div className="card">
                    <div className="wrapper">
                      {[
                        ["What is your return policy?", "Eligible items can be returned within the stated timeframe for a refund or exchange. See the tabs above for full details."],
                        ["How long do I have to return an item?", "Typically, you have 14 days from delivery to initiate a return request."],
                        ["Can I return an item if I changed my mind?", "Yes, if the item is unused and in original condition with packaging intact."],
                        ["How do I initiate a return?", "Log in, open your order history, select the item, and follow the return prompts."],
                        ["What if my item arrives damaged or defective?", `Contact ${SUPPORT_EMAIL} immediately and we will arrange a replacement or refund.`],
                      ].map(([q, a]) => (
                        <span key={q}>
                          <h3>{q}</h3>
                          <p>{a}</p>
                        </span>
                      ))}
                    </div>
                    <div className="wrapper">
                      {[
                        ["Do I have to pay for return shipping?", "Return shipping depends on the reason for return. See our policy for details."],
                        ["How long does processing take?", "After we receive the item, processing usually takes 10–14 business days."],
                        ["Can I exchange instead of returning?", "Yes, you can request an exchange when initiating your return where stock allows."],
                        ["Do you offer returns for international orders?", "International returns may be available; shipping fees and duties may apply."],
                        ["Still need help?", `Email ${SUPPORT_EMAIL} and our support team will assist you.`],
                      ].map(([q, a]) => (
                        <span key={q}>
                          <h3>{q}</h3>
                          <p>{a}</p>
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "requirement" && (
                <div className="return-requirement">
                  <h3>Requirements for returns</h3>
                  <div className="card">
                    <table>
                      <thead>
                        <tr>
                          <th>Reason for return</th>
                          <th>Description</th>
                          <th>Tags / labels</th>
                          <th>Original packaging</th>
                          <th>Accessories &amp; gifts</th>
                          <th>New condition</th>
                          <th>Not damaged</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Changed mind</td>
                          <td>Product unused (fashion category only).</td>
                          <td>ok</td><td>ok</td><td>ok</td><td>ok</td><td>yes</td>
                        </tr>
                        <tr>
                          <td>Wrong item</td>
                          <td>Item differs from what was shown on the website.</td>
                          <td>ok</td><td>ok</td><td>ok</td><td>ok</td><td>yes</td>
                        </tr>
                        <tr>
                          <td>Incomplete item</td>
                          <td>Order delivered partially compared to the website listing.</td>
                          <td>no</td><td>ok</td><td>ok</td><td>ok</td><td>yes</td>
                        </tr>
                        <tr>
                          <td>Defective item</td>
                          <td>Manufacturer defect or dead on arrival after validation.</td>
                          <td>no</td><td>ok</td><td>ok</td><td>ok</td><td>yes</td>
                        </tr>
                        <tr>
                          <td>Damaged in transit</td>
                          <td>Visible transit damage; report within 24 hours.</td>
                          <td>ok</td><td>ok</td><td>ok</td><td>ok</td><td>yes</td>
                        </tr>
                        <tr>
                          <td>Product standard</td>
                          <td>Product does not perform to specification after validation.</td>
                          <td>ok</td><td>ok</td><td>ok</td><td>ok</td><td>yes</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </MarketplacePage>
    </Fragment>
  );
};

export default Returnpolicy;
